import { UPLOAD_PURPOSE_MARKETPLACE_PARTNER_PREFLIGHT, UPLOAD_POLICIES } from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";
import {
  putToSignedUrlWithProgress,
  stageMemoryPreflightUpload,
  type UploadConfirmResult,
  type UploadPresignResult,
} from "@/features/preflight/api/uploads.api";
import { resolvePartnerPreflightContentType } from "@/features/marketplace/lib/resolve-partner-preflight-content-type";

const PARTNER_UPLOAD_POLICY = UPLOAD_POLICIES[UPLOAD_PURPOSE_MARKETPLACE_PARTNER_PREFLIGHT];

async function presignPartnerVideoUpload(input: {
  tenantId: string;
  contentType: string;
  sizeBytes: number;
  originalName: string;
  signal?: AbortSignal;
}): Promise<UploadPresignResult> {
  const { data } = await authFetch<UploadPresignResult>("/uploads/presign", {
    method: "POST",
    signal: input.signal,
    body: JSON.stringify({
      tenantId: input.tenantId,
      purpose: UPLOAD_PURPOSE_MARKETPLACE_PARTNER_PREFLIGHT,
      contentType: input.contentType,
      sizeBytes: input.sizeBytes,
      originalName: input.originalName,
    }),
  });
  return data;
}

async function confirmPartnerVideoUpload(input: {
  tenantId: string;
  uploadId: string;
  signal?: AbortSignal;
}): Promise<UploadConfirmResult> {
  const { data } = await authFetch<UploadConfirmResult>("/uploads/confirm", {
    method: "POST",
    signal: input.signal,
    body: JSON.stringify(input),
  });
  return data;
}

export type PartnerPreflightUploadPhase = "preparing" | "uploading" | "confirming";

/**
 * presign → upload (or stage) → confirm. `onProgress` reports 0–100 for a
 * direct-to-storage PUT; the stage-memory fallback jumps to 100 on completion.
 */
export async function runPartnerPreflightUploadFlow(input: {
  tenantId: string;
  file: File;
  onProgress?: (percent: number) => void;
  onPhase?: (phase: PartnerPreflightUploadPhase) => void;
  signal?: AbortSignal;
}): Promise<UploadConfirmResult> {
  input.onPhase?.("preparing");
  const contentType = resolvePartnerPreflightContentType(input.file, PARTNER_UPLOAD_POLICY);
  const presign = await presignPartnerVideoUpload({
    tenantId: input.tenantId,
    contentType,
    sizeBytes: input.file.size,
    originalName: input.file.name,
    signal: input.signal,
  });

  input.onPhase?.("uploading");
  if (presign.requiresMemoryStage || presign.requiresServerStage) {
    await stageMemoryPreflightUpload({
      tenantId: input.tenantId,
      uploadId: presign.uploadId,
      contentType,
      file: input.file,
      signal: input.signal,
    });
    input.onProgress?.(100);
  } else {
    await putToSignedUrlWithProgress(presign.uploadUrl, input.file, presign.requiredHeaders, {
      onProgress: input.onProgress,
      signal: input.signal,
    });
  }

  input.onPhase?.("confirming");
  return confirmPartnerVideoUpload({
    tenantId: input.tenantId,
    uploadId: presign.uploadId,
    signal: input.signal,
  });
}
